import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from '../components/LanguageSelector';

const { FiMail, FiCheckCircle, FiAlertCircle, FiCode } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const { signInWithMagicLink, signInDemo, session } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (session) {
      navigate('/', { replace: true });
    }
  }, [session, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    const result = await signInWithMagicLink(email);

    if (result.success) {
      setStatus('success');
      setEmail('');
    } else {
      setStatus('error');
      setErrorMessage(result.error || t('login.errorMessage'));
    }
  };

  const handleDemoLogin = async () => {
    setStatus('loading');
    setErrorMessage('');

    const result = await signInDemo();

    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setStatus('error');
      setErrorMessage('Failed to start demo mode');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4">
      <div className="flex items-stretch gap-12 w-full max-w-5xl px-4">
        {/* Logo face */}
        <div className="hidden md:flex items-center justify-center flex-1">
          <img
            src="/logos/logo-face.png"
            alt="Efficacitas"
            className="h-full w-auto object-contain max-h-[600px] opacity-90"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md flex"
        >
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 flex-1 flex flex-col justify-center">
          {/* Language Selector */}
          <div className="flex justify-end mb-4">
            <LanguageSelector />
          </div>

          {/* Logo/Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
              {t('login.title')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('login.subtitle')}
            </p>
          </div>

          {/* Success Message */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3"
            >
              <SafeIcon icon={FiCheckCircle} className="text-green-600 dark:text-green-400 text-xl flex-shrink-0 mt-0.5" />
              <p className="text-green-800 dark:text-green-200 text-sm">
                {t('login.successMessage')}
              </p>
            </motion.div>
          )}

          {/* Error Message */}
          {status === 'error' && errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3"
            >
              <SafeIcon icon={FiAlertCircle} className="text-red-600 dark:text-red-400 text-xl flex-shrink-0 mt-0.5" />
              <p className="text-red-800 dark:text-red-200 text-sm">
                {errorMessage}
              </p>
            </motion.div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('login.emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-slate-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  disabled={status === 'loading' || status === 'success'}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  required
                />
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={status === 'loading' || status === 'success'}
              whileHover={{ scale: status === 'loading' || status === 'success' ? 1 : 1.02 }}
              whileTap={{ scale: status === 'loading' || status === 'success' ? 1 : 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('login.signingIn')}</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiMail} />
                  <span>{t('login.submitButton')}</span>
                </>
              )}
            </motion.button>
          </form>

          {/* Demo Mode Button */}
          <div className="mt-4">
            <motion.button
              type="button"
              onClick={handleDemoLogin}
              disabled={status === 'loading'}
              whileHover={{ scale: status === 'loading' ? 1 : 1.02 }}
              whileTap={{ scale: status === 'loading' ? 1 : 0.98 }}
              className="w-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiCode} />
              <span>{t('login.demoMode')}</span>
            </motion.button>

            <p className="mt-2 text-xs text-center text-slate-500 dark:text-slate-400">
              {t('login.demoDescription')}
            </p>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              <Trans
                i18nKey="login.termsAndPrivacy"
                components={{
                  privacyLink: (
                    <Link
                      to="/privacy"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </div>
  );
};

export default Login;